import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import {
  HEX_COLOR_PATTERN,
  type DomainVerificationInstruction,
  type DomainVerificationState,
  type InstitutionBranding,
  type UpdateBrandingRequest,
} from '@/types/platform';
import { formatDateTime } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * PA-06 — how an institution appears to its own users.
 *
 * THIS FORM COULD NEVER SAVE. It POSTed to `/branding/:id` with a `reason` field
 * and a set of names core has never had — `logoUrl`, `faviconUrl`, `displayName`,
 * `customDomain`. The endpoint is a PUT, the DTO forbids unrecognised fields, and
 * the custom domain has its own endpoint entirely. Every one of those is fatal on
 * its own; together they meant no branding change from this console has ever
 * reached the database.
 *
 * IT ALSO READ THE WRONG ROUTE. `GET /branding/:id` returns RESOLVED branding with
 * platform defaults already applied. That is correct for a portal painting a page
 * and wrong for an editor: every default arrives looking like a value this
 * institution chose, and saving the form would have written the platform defaults
 * in as this institution's own overrides. The editor reads `:id/settings`, which
 * core documents as being for exactly this screen and which keeps its nulls.
 *
 * CONTRAST IS CHECKED HERE, NOT LEFT TO TASTE. A brand colour is chosen from a logo
 * and applied to button text, and the result is frequently unreadable. Nobody
 * notices, because the person choosing it knows what the button says. So the ratio
 * against white and against the console's dark surface is computed as the value is
 * typed and shown against WCAG's 4.5:1 threshold — not as a veto, because a colour
 * may be intended for a large heading where 3:1 applies, but as a number the
 * chooser has to look at. The server grades the merged result and refuses outright.
 *
 * IMAGES ARE NOT EDITED HERE. They are object keys plus a measurement the uploader
 * took, not URLs, and there is no upload endpoint in this console. What is shown is
 * what is stored, so an administrator can see whether a logo exists.
 */

const EMPTY: UpdateBrandingRequest = {
  primaryColor: '',
  secondaryColor: '',
  accentColor: '',
  loginBackgroundColor: '',
  portalTitle: '',
  defaultLanguage: '',
  supportEmail: '',
  supportPhone: '',
  supportUrl: '',
  emailSenderName: '',
  emailSenderAddress: '',
  loginHeadline: '',
  loginSubtitle: '',
  subdomain: '',
};

const COLOUR_FIELDS = [
  'primaryColor',
  'secondaryColor',
  'accentColor',
  'loginBackgroundColor',
] as const;

const TEXT_FIELDS = [
  'portalTitle',
  'defaultLanguage',
  'supportEmail',
  'supportPhone',
  'supportUrl',
  'emailSenderName',
  'emailSenderAddress',
  'loginHeadline',
  'loginSubtitle',
  'subdomain',
] as const;

const DOMAIN_TONES: Record<DomainVerificationState, string> = {
  NOT_CONFIGURED: 'mutedTag',
  PENDING_DNS: 'warnTag',
  VERIFIED: 'okTag',
  FAILED: 'dangerTag',
};

export function InstitutionBrandingPanel({ institutionId }: { institutionId: string }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const fetcher = useCallback(() => platformApi.branding.settings(institutionId), [institutionId]);
  const { state, reload, replace } = useAsyncData<InstitutionBranding>(
    `branding:${institutionId}`,
    fetcher,
    institutionId.length > 0,
  );

  const [form, setForm] = useState<UpdateBrandingRequest>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (state.status !== 'ready') return;
    const stored = state.data;
    const next: UpdateBrandingRequest = { ...EMPTY };
    for (const field of COLOUR_FIELDS) next[field] = stored[field] ?? '';
    for (const field of TEXT_FIELDS) next[field] = stored[field] ?? '';
    setForm(next);
  }, [state]);

  const coloursValid = COLOUR_FIELDS.every((field) => {
    const value = form[field] ?? '';
    return value.length === 0 || HEX_COLOR_PATTERN.test(value);
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!coloursValid || submitting) return;
    setSubmitting(true);
    setError(null);
    setSaved(false);
    try {
      // Blank inputs are dropped rather than sent as empty strings: an empty
      // string is a value, and it would overwrite a stored setting with nothing
      // rather than leaving it alone. Clearing is `restore-defaults`, which asks
      // why, because it is destructive of a chosen palette.
      const patch: UpdateBrandingRequest = {};
      for (const [key, value] of Object.entries(form) as [keyof UpdateBrandingRequest, unknown][]) {
        if (typeof value === 'string' && value.trim().length > 0) {
          Object.assign(patch, { [key]: value.trim() });
        }
      }
      replace(await platformApi.branding.update(institutionId, patch));
      setSaved(true);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Panel
        title={t('platform.branding.title')}
        icon="star"
        actions={
          <button type="button" className={styles.secondaryBtn} onClick={reload}>
            <Icon name="refresh" size={15} />
            {t('platform.dashboard.refresh')}
          </button>
        }
      >
        <AsyncPanel state={state} onRetry={reload}>
          {(stored) => (
            <form className={styles.dialogForm} onSubmit={submit} noValidate>
              <p className={styles.fieldHint}>{t('platform.branding.lead')}</p>

              <div className={styles.dialogRow}>
                {COLOUR_FIELDS.map((field) => (
                  <ColourField
                    key={field}
                    label={t(`platform.branding.${field}`)}
                    value={form[field] ?? ''}
                    onChange={(value) => setForm({ ...form, [field]: value })}
                  />
                ))}
              </div>

              <div className={styles.dialogRow}>
                <TextField
                  label={t('platform.branding.portalTitle')}
                  hint={t('platform.branding.portalTitleHint')}
                  value={form.portalTitle ?? ''}
                  onChange={(value) => setForm({ ...form, portalTitle: value })}
                />
                <TextField
                  label={t('platform.branding.defaultLanguage')}
                  hint={t('platform.branding.defaultLanguageHint')}
                  value={form.defaultLanguage ?? ''}
                  onChange={(value) => setForm({ ...form, defaultLanguage: value })}
                />
              </div>

              <div className={styles.dialogRow}>
                <TextField
                  label={t('platform.branding.loginHeadline')}
                  value={form.loginHeadline ?? ''}
                  onChange={(value) => setForm({ ...form, loginHeadline: value })}
                />
                <TextField
                  label={t('platform.branding.loginSubtitle')}
                  value={form.loginSubtitle ?? ''}
                  onChange={(value) => setForm({ ...form, loginSubtitle: value })}
                />
              </div>

              <div className={styles.dialogRow}>
                <TextField
                  label={t('platform.branding.supportEmail')}
                  type="email"
                  value={form.supportEmail ?? ''}
                  onChange={(value) => setForm({ ...form, supportEmail: value })}
                />
                <TextField
                  label={t('platform.branding.supportPhone')}
                  value={form.supportPhone ?? ''}
                  onChange={(value) => setForm({ ...form, supportPhone: value })}
                />
                <TextField
                  label={t('platform.branding.supportUrl')}
                  type="url"
                  value={form.supportUrl ?? ''}
                  onChange={(value) => setForm({ ...form, supportUrl: value })}
                />
              </div>

              <div className={styles.dialogRow}>
                <TextField
                  label={t('platform.branding.emailSenderName')}
                  value={form.emailSenderName ?? ''}
                  onChange={(value) => setForm({ ...form, emailSenderName: value })}
                />
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('platform.branding.emailSenderAddress')}</span>
                  <input
                    className={styles.input}
                    type="email"
                    value={form.emailSenderAddress ?? ''}
                    onChange={(event) => setForm({ ...form, emailSenderAddress: event.target.value })}
                  />
                  {/*
                    Verification is not a formality: an unverified sender is
                    refused downstream, and the address will look correct on this
                    screen while every email silently fails to leave.
                  */}
                  <span className={styles.fieldHint}>
                    {stored.emailSenderVerified
                      ? t('platform.branding.senderVerified')
                      : t('platform.branding.senderUnverified')}
                  </span>
                </label>
              </div>

              <TextField
                label={t('platform.branding.subdomain')}
                hint={t('platform.branding.subdomainHint')}
                value={form.subdomain ?? ''}
                onChange={(value) => setForm({ ...form, subdomain: value })}
              />

              <dl className={styles.detailGrid}>
                <dt>{t('platform.branding.logoStored')}</dt>
                <dd>{describeAsset(stored.logoObjectKey, stored.logoWidthPx, stored.logoHeightPx, t)}</dd>
                <dt>{t('platform.branding.faviconStored')}</dt>
                <dd>{stored.faviconObjectKey ?? t('platform.branding.noAsset')}</dd>
                {/*
                  Read-only, and shown so nobody goes looking for the switch. The
                  attribution is enforced by a database CHECK as well as by the
                  API, because only the last survives a future endpoint written in
                  good faith.
                */}
                <dt>{t('platform.branding.attribution')}</dt>
                <dd>
                  {stored.poweredByAttribution === false
                    ? t('platform.branding.attributionOff')
                    : t('platform.branding.attributionOn')}
                </dd>
                <dt>{t('platform.branding.lastChanged')}</dt>
                <dd>
                  {formatDateTime(stored.updatedAt, locale)}
                  {stored.lastChangedBy ? ` · ${stored.lastChangedBy}` : ''}
                </dd>
              </dl>

              {error && (
                <p className={styles.formError} role="alert">
                  {error}
                </p>
              )}
              {saved && (
                <p className={styles.okNotice} role="status">
                  {t('platform.branding.saved')}
                </p>
              )}

              <div className={styles.transitionRow}>
                <button type="submit" className={styles.primaryBtn} disabled={!coloursValid || submitting}>
                  {submitting ? t('platform.confirm.submitting') : t('common.save')}
                </button>
              </div>
            </form>
          )}
        </AsyncPanel>
      </Panel>

      <CustomDomainPanel institutionId={institutionId} onChanged={reload} locale={locale} />
    </>
  );
}

function describeAsset(
  key: string | null | undefined,
  width: number | null | undefined,
  height: number | null | undefined,
  t: (key: string) => string,
): string {
  if (!key) return t('platform.branding.noAsset');
  return width && height ? `${key} (${width}×${height})` : key;
}

function TextField({
  label,
  hint,
  value,
  type = 'text',
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  type?: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <input
        className={styles.input}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {hint && <span className={styles.fieldHint}>{hint}</span>}
    </label>
  );
}

/**
 * The custom domain, which is a claim and a proof rather than a text field.
 *
 * Nothing reaches VERIFIED except through a check that passed. There is no
 * administrative override and this panel does not offer one, because downstream an
 * override is indistinguishable from a verified domain — and the domain is not
 * served until it verifies, so an override would produce a domain that looks live
 * on this screen and answers nothing.
 */
function CustomDomainPanel({
  institutionId,
  onChanged,
  locale,
}: {
  institutionId: string;
  onChanged: () => void;
  locale: string;
}) {
  const { t } = useTranslation();
  const [domain, setDomain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [verifiedAt, setVerifiedAt] = useState<string | null>(null);

  const fetcher = useCallback(() => platformApi.branding.domainInstruction(institutionId), [institutionId]);
  const { state, reload, replace } = useAsyncData<DomainVerificationInstruction>(
    `branding-domain:${institutionId}`,
    fetcher,
    institutionId.length > 0,
  );

  const claim = async (event: React.FormEvent) => {
    event.preventDefault();
    if (domain.trim().length === 0 || busy) return;
    setBusy(true);
    setError(null);
    setFailure(null);
    try {
      replace(await platformApi.branding.claimDomain(institutionId, domain.trim().toLowerCase()));
      setDomain('');
      onChanged();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError(null);
    setFailure(null);
    try {
      const branding = await platformApi.branding.verifyDomain(institutionId);
      // A failed check is not an error: we looked, and the record was not there.
      // The reason the server recorded says what the zone actually published,
      // which is the difference between "retry" and "you published the wrong value".
      setFailure(branding.customDomainFailureReason ?? null);
      setVerifiedAt(branding.customDomainVerifiedAt ?? null);
      reload();
      onChanged();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title={t('platform.branding.domainTitle')} icon="globe">
      <p className={styles.dataNotice}>
        <Icon name="info" size={15} aria-hidden="true" />
        {t('platform.branding.domainNotice')}
      </p>

      <AsyncPanel state={state} onRetry={reload}>
        {(instruction) => (
          <>
            <dl className={styles.detailGrid}>
              <dt>{t('platform.branding.domainLabel')}</dt>
              <dd>
                {instruction.domain || '—'}{' '}
                <span className={styles[DOMAIN_TONES[instruction.state]] ?? styles.mutedTag}>
                  {t(`platform.branding.domainState.${instruction.state}`, { defaultValue: instruction.state })}
                </span>
              </dd>
              {instruction.state !== 'NOT_CONFIGURED' && (
                <>
                  <dt>{t('platform.branding.recordName')}</dt>
                  <dd>
                    <code className={styles.code}>{instruction.recordName}</code>
                  </dd>
                  <dt>{t('platform.branding.recordValue')}</dt>
                  <dd>
                    <code className={styles.code}>
                      {instruction.recordType} {instruction.recordValue}
                    </code>
                  </dd>
                </>
              )}
              {verifiedAt && (
                <>
                  <dt>{t('platform.branding.verifiedAt')}</dt>
                  <dd>{formatDateTime(verifiedAt, locale)}</dd>
                </>
              )}
            </dl>

            {failure && (
              <p className={styles.rowDetailError} role="status">
                {t('platform.branding.domainFailure', { reason: failure })}
              </p>
            )}

            {instruction.state !== 'NOT_CONFIGURED' && (
              <div className={styles.transitionRow}>
                <button type="button" className={styles.secondaryBtn} onClick={verify} disabled={busy}>
                  <Icon name="refresh" size={15} />
                  {busy ? t('platform.confirm.submitting') : t('platform.branding.verifyDomain')}
                </button>
              </div>
            )}
          </>
        )}
      </AsyncPanel>

      <form className={styles.dialogForm} onSubmit={claim} noValidate>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.branding.claimLabel')}</span>
          <input
            className={styles.input}
            value={domain}
            autoComplete="off"
            placeholder="portal.example.ac.ug"
            onChange={(event) => setDomain(event.target.value)}
          />
          <span className={styles.fieldHint}>{t('platform.branding.claimHint')}</span>
        </label>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className={styles.transitionRow}>
          <button type="submit" className={styles.secondaryBtn} disabled={domain.trim().length === 0 || busy}>
            {t('platform.branding.claimDomain')}
          </button>
        </div>
      </form>
    </Panel>
  );
}

/**
 * A hex colour with its contrast ratios stated.
 *
 * Both ratios matter because the same colour is used in the institution's own
 * light workspace and, for CSKY staff, alongside this dark console. A colour that
 * passes on one and fails on the other is the normal case, not an edge case.
 */
function ColourField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const valid = value.length === 0 || HEX_COLOR_PATTERN.test(value);
  const ratios = valid && value.length > 0 ? contrastRatios(value) : null;

  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <div className={styles.colourRow}>
        <input
          className={styles.input}
          value={value}
          placeholder="#1A4FA0"
          autoComplete="off"
          spellCheck={false}
          aria-invalid={!valid}
          onChange={(event) => onChange(event.target.value)}
        />
        <span
          className={styles.swatch}
          style={valid && value ? { background: value } : undefined}
          aria-hidden="true"
        />
      </div>
      {!valid && (
        <span className={styles.fieldError} role="alert">
          {t('platform.branding.invalidColour')}
        </span>
      )}
      {ratios && (
        <span className={styles.fieldHint}>
          <Icon name={ratios.onLight >= 4.5 && ratios.onDark >= 4.5 ? 'check' : 'warning'} size={13} />{' '}
          {t('platform.branding.contrast', {
            light: ratios.onLight.toFixed(2),
            dark: ratios.onDark.toFixed(2),
          })}
        </span>
      )}
    </label>
  );
}

/** WCAG 2.1 relative luminance, then the standard (L1+0.05)/(L2+0.05) ratio. */
function contrastRatios(hex: string): { onLight: number; onDark: number } {
  const luminance = relativeLuminance(hex);
  const white = 1;
  // The console surface, matching the dark chrome these colours sit beside.
  const dark = relativeLuminance('#0B121F');
  return {
    onLight: (Math.max(luminance, white) + 0.05) / (Math.min(luminance, white) + 0.05),
    onDark: (Math.max(luminance, dark) + 0.05) / (Math.min(luminance, dark) + 0.05),
  };
}

function relativeLuminance(hex: string): number {
  const normalized =
    hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex;
  const channels = [1, 3, 5].map((offset) => Number.parseInt(normalized.slice(offset, offset + 2), 16) / 255);
  const [r, g, b] = channels.map((channel) =>
    channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4,
  );
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
